import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Table,
} from "reactstrap";
import { BookingApi } from "../../apis/BookingApi";

const ViewBooking = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isRTL = i18n.language === "ar";

  useEffect(() => {
    if (!id) return;

    setLoading(true);

    BookingApi.getBookingById(id)
      .then((res) => {
        setBooking(res.data?.data);
        setLoading(false);
      })
      .catch(() => {
        setError(t("Failed to load booking details."));
        setLoading(false);
      });
  }, [id, t]);

  const renderRow = (label, value) => (
    <div
      className={`d-flex mb-2 ${
        isRTL ? "flex-row-reverse text-end" : "flex-row text-start"
      }`}
    >
      <div style={{ minWidth: "180px", fontWeight: "600" }}>
        {t(label)} :
      </div>

      <div style={{ flex: 1 }}>{value || "-"}</div>
    </div>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner color="primary" />
        <p>{t("Loading booking details...")}</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="text-center mt-5">
        <p className="text-danger">{error}</p>
      </Container>
    );
  }

  if (!booking) {
    return (
      <Container className="text-center mt-5">
        <p>{t("No booking found.")}</p>
      </Container>
    );
  }

  return (
    <Container
      className="mt-5"
      dir={isRTL ? "rtl" : "ltr"}
      style={{ textAlign: isRTL ? "right" : "left" }}
    >
      <Row>
        <Col md={{ size: 10, offset: 1 }}>
         <br /><br /><br />
          <Card>
            <CardBody>

              {/* Title */}
              <h3 className={`mb-3 ${isRTL ? "text-end" : "text-start"}`}>
                {t("Booking Details")}
              </h3>

              <hr />

              {/* Details */}
              {renderRow("Booking ID", booking.booking_id)}

              {renderRow(
                "Customer Name",
                [
                  booking.firstname,
                  booking.middlename,
                  booking.lastname,
                ]
                  .filter(Boolean)
                  .join(" ")
              )}

              {renderRow(
                "Phone",
                `+${booking.country_phone_code} ${booking.phone_number}`
              )}

              {renderRow("Salon", booking.salon_name)}

              {renderRow(
                "Salon Admin",
                [
                  booking.salon_admin_firstname,
                  booking.salon_admin_middlename,
                  booking.salon_admin_lastname,
                ]
                  .filter(Boolean)
                  .join(" ")
              )}

              {renderRow("Date", booking.booking_date)}

              {renderRow("Time", booking.min_time)}

              {renderRow("Status", booking.booking_status)}

              {renderRow(
                "Payment",
                `${booking.payment_status} (${booking.payment_option})`
              )}

              {renderRow(
                "Amount",
                `${t("SAR")} ${booking.amount}`
              )}

              {renderRow(
                "Discount / Coupon",
                `${booking.coupon_code || "-"} (${t("SAR")} ${
                  booking.coupon_amount || 0
                })`
              )}

              {renderRow(
                "Sub Total",
                `${t("SAR")} ${booking.sub_total}`
              )}

              {renderRow("Service At", booking.service_at)}

              {renderRow("Address", booking.address)}

              {renderRow("Landmark", booking.landmark)}

              {renderRow("Address Type", booking.address_type)}

              <hr />

              {/* Services */}
              <h5 className={isRTL ? "text-end" : "text-start"}>
                {t("Services Booked")}
              </h5>

              <Table
                bordered
                responsive
                className={isRTL ? "text-end" : "text-start"}
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("Service")}</th>
                    <th>{t("Employee")}</th>
                    <th>{t("Date")}</th>
                    <th>{t("Start - End")}</th>
                    <th>{t("Price")}</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>

                <tbody>
                  {booking.bookingPersons?.map((person, i) => (
                    <tr key={person.id}>
                      <td>{i + 1}</td>

                      <td>{person.sub_service_name}</td>

                      <td>
                        {[
                          person.emp_firstname,
                          person.emp_middlename,
                          person.emp_lastname,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </td>

                      <td>{person.booking_date}</td>

                      <td>
                        {person.start_time} - {person.end_time}
                      </td>

                      <td>
                        {t("SAR")} {person.price}
                      </td>

                      <td>{person.booking_service_status}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ViewBooking;
